import React, { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Modal,
  Tab,
  Tabs,
  Table,
  Dropdown,
  ButtonGroup,
} from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCertificate,
  faCheckCircle,
  faCopy,
  faEllipsisH,
  faEdit,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { BaseUrl } from '../Routes/BaseUrl';
import CommonDeleteModal from '../components/CommonDeleteModal';

const today = () => new Date().toISOString().split('T')[0];

const ROLES = [
  'MERN Stack Developer Intern',
  'DevOps Intern',
  'AI Intern',
  'Cyber Security Intern',
  'UI/UX Design Intern',
  'Data Science Intern',
  'Mobile App Developer Intern',
  'Human Resources (HR)',
  'Business Development',
  'Digital Marketing',
  'Project Management',
  'Content Writing',
  'Social Media manager',
  'Graphic Designing',
  'Video Editing',
  'Full Stack Developer',
  'Frontend Developer',
  'Backend Developer',
  'Software Development (AI/ML Focus)',
  'DevOps',
  'SEO',
  'Data Analyst',
  'Finance and Accounting',
  'React Native',
];

const defaultForm = {
  role: ROLES[0],
  issuedTo: '',
  issuedDate: today(),
};

const IssueCertificate = () => {
  const [form, setForm]           = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState('');
  const [issuedCert, setIssuedCert] = useState(null); // holds the response data on success
  const [copied, setCopied]       = useState(false);
  const [activeTab, setActiveTab] = useState('issue');
  const [certificates, setCertificates] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCertId, setSelectedCertId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    certificateId: '',
    internName: '',
    domain: '',
    issueDate: '',
    issuedBy: '',
  });
  const [editError, setEditError] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);

  const fetchCertificates = async () => {
    setListLoading(true);
    setListError('');

    try {
      const response = await axios.get(
        `${BaseUrl}/api/admin/certificates`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      setCertificates(response.data?.data || []);
    } catch (err) {
      if (err.request && !err.response) {
        setListError('Network error: Could not reach the server.');
        return;
      }
      if (err.response) {
        setListError(err.response.data?.message || `Server error (${err.response.status})`);
        return;
      }
      setListError(err.message || 'An unexpected error occurred.');
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchCertificates();
  }, []);

  const handleDeleteClick = (id) => {
    setSelectedCertId(id);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedCertId(null);
  };

  const handleDeleteSuccess = async () => {
    setShowDeleteModal(false);
    setSelectedCertId(null);
    await fetchCertificates();
  };

  const openEditModal = (cert) => {
    setEditForm({
      certificateId: cert.certificateId || '',
      internName: cert.internName || '',
      domain: cert.domain || '',
      issueDate: cert.issueDate ? cert.issueDate.split('T')[0] : '',
      issuedBy: cert.issuedBy || 'Avijo',
    });
    setEditError('');
    setSelectedCertId(cert._id);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedCertId(null);
    setEditError('');
  };

  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
    setEditError('');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.internName.trim()) {
      setEditError('Intern name is required.');
      return;
    }
    if (!editForm.domain.trim()) {
      setEditError('Role is required.');
      return;
    }
    if (!editForm.issueDate) {
      setEditError('Issued date is required.');
      return;
    }

    setEditSubmitting(true);
    try {
      await axios.put(
        `${BaseUrl}/api/admin/certificates/${selectedCertId}`,
        {
          certificateId: editForm.certificateId.trim(),
          internName: editForm.internName.trim(),
          domain: editForm.domain.trim(),
          issueDate: editForm.issueDate,
          issuedBy: editForm.issuedBy.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      await fetchCertificates();
      closeEditModal();
    } catch (err) {
      if (err.request && !err.response) {
        setEditError('Network error: Could not reach the server.');
      } else if (err.response) {
        setEditError(err.response.data?.message || `Server error (${err.response.status})`);
      } else {
        setEditError(err.message || 'An unexpected error occurred.');
      }
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validate = () => {
    if (!form.issuedTo.trim()) return 'Intern name is required.';
    if (!form.issuedDate)      return 'Issued date is required.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setSubmitting(true);
    setError('');

    try {
      const response = await axios.post(
        `${BaseUrl}/api/admin/issue-certificate`,
        {
          internName: form.issuedTo.trim(),
          domain:     form.role,
          issueDate:  form.issuedDate,
        },
        {
          headers: {
            Authorization:  `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 201) {
        setIssuedCert(response.data?.data);
        // Clear intern name and role; keep the date for the next issuance
        setForm(prev => ({ ...prev, issuedTo: '', role: ROLES[0] }));
        fetchCertificates();
      }
    } catch (err) {
      if (err.request && !err.response) {
        console.error('[IssueCertificate] Network Error:', err);
        setError('Network error: Could not reach the server. Check that the backend is running.');
        return;
      }
      if (err.response) {
        console.error('[IssueCertificate] Server error:', err.response.status, err.response.data);
        setError(err.response.data?.message || `Server error (${err.response.status})`);
        return;
      }
      console.error('[IssueCertificate] Unexpected error:', err.message);
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyId = () => {
    if (!issuedCert?.certificateId) return;
    navigator.clipboard.writeText(issuedCert.certificateId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCloseSuccess = () => {
    setIssuedCert(null);
    setCopied(false);
  };

  return (
    <Container fluid>
      <Row className="justify-content-center">
        <Col xs={12} xl={10}>
          <Card border="light" className="bg-white shadow-sm mb-4">
            <Card.Header className="border-bottom">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faCertificate} className="me-2 text-primary" />
                <h5 className="mb-0">Certificates</h5>
              </div>
            </Card.Header>
            <Card.Body>
              <Tabs
                activeKey={activeTab}
                onSelect={(key) => setActiveTab(key || 'issue')}
                className="mb-4"
              >
                <Tab eventKey="issue" title="Issue Certificate">
                  {error && (
                    <Alert variant="danger" dismissible onClose={() => setError('')}>
                      {error}
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <Row>
                      {/* Role */}
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Heading / Role <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Select
                            value={form.role}
                            onChange={(e) => handleChange('role', e.target.value)}
                          >
                            {ROLES.map((r) => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      {/* Issued To */}
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Issued To <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Intern's full name"
                            value={form.issuedTo}
                            onChange={(e) => handleChange('issuedTo', e.target.value)}
                          />
                        </Form.Group>
                      </Col>

                      {/* Issued Date */}
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Issued Date</Form.Label>
                          <Form.Control
                            type="date"
                            value={form.issuedDate}
                            onChange={(e) => handleChange('issuedDate', e.target.value)}
                          />
                        </Form.Group>
                      </Col>

                      {/* Issued By — read-only */}
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Issued By</Form.Label>
                          <Form.Control
                            type="text"
                            value="Avijo"
                            readOnly
                            className="bg-light"
                          />
                        </Form.Group>
                      </Col>

                      {/* Status — read-only */}
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Status</Form.Label>
                          <Form.Control
                            type="text"
                            value="VALID"
                            readOnly
                            className="bg-light fw-bold text-success"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-end mt-2">
                      <Button variant="primary" type="submit" disabled={submitting}>
                        {submitting ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Issuing...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faCertificate} className="me-2" />
                            Issue Certificate
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </Tab>

                <Tab eventKey="list" title="Issued Certificates">
                  <div className="d-flex justify-content-end mb-3">
                    <Button
                      variant="outline-primary"
                      onClick={fetchCertificates}
                      disabled={listLoading}
                    >
                      {listLoading ? 'Refreshing...' : 'Refresh'}
                    </Button>
                  </div>

                  {listError && (
                    <Alert variant="danger" dismissible onClose={() => setListError('')}>
                      {listError}
                    </Alert>
                  )}

                  <div className="table-responsive certificate-table-wrap">
                    <Table hover className="mb-0">
                      <thead>
                        <tr>
                          <th>Certificate ID</th>
                          <th>Name</th>
                          <th>Role</th>
                          <th>Issued Date</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {listLoading && (
                          <tr>
                            <td colSpan={6} className="text-center text-muted py-4">
                              Loading certificates...
                            </td>
                          </tr>
                        )}

                        {!listLoading && certificates.length === 0 && (
                          <tr>
                            <td colSpan={6} className="text-center text-muted py-4">
                              No certificates issued yet.
                            </td>
                          </tr>
                        )}

                        {!listLoading && certificates.map((cert) => (
                          <tr key={cert._id}>
                            <td>{cert.certificateId}</td>
                            <td>{cert.internName}</td>
                            <td>{cert.domain}</td>
                            <td>
                              {cert.issueDate
                                ? new Date(cert.issueDate).toLocaleDateString()
                                : '—'}
                            </td>
                            <td className={cert.status === 'VALID' ? 'text-success' : 'text-muted'}>
                              {cert.status === 'VALID' ? 'Active' : 'Inactive'}
                            </td>
                            <td>
                              <Dropdown as={ButtonGroup}>
                                <Dropdown.Toggle
                                  as={Button}
                                  split
                                  variant="link"
                                  className="text-dark m-0 p-0"
                                >
                                  <span className="icon icon-sm">
                                    <FontAwesomeIcon icon={faEllipsisH} className="icon-dark" />
                                  </span>
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="certificate-action-menu">
                                  <Dropdown.Item onClick={() => openEditModal(cert)}>
                                    <FontAwesomeIcon icon={faEdit} className="me-2" /> Edit
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => handleDeleteClick(cert._id)}
                                  >
                                    <FontAwesomeIcon icon={faTrashAlt} className="me-2" /> Remove
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── Success Modal ── */}
      <Modal show={!!issuedCert} onHide={handleCloseSuccess} centered>
        <Modal.Body className="text-center p-5">
          <FontAwesomeIcon
            icon={faCheckCircle}
            className="text-success mb-3"
            style={{ fontSize: '3rem' }}
          />
          <h4 className="mb-1">Certificate Issued!</h4>
          <p className="text-muted mb-4">
            Successfully issued to <strong>{issuedCert?.internName}</strong>
          </p>

          {/* Generated ID highlight box */}
          <div
            className="d-flex align-items-center justify-content-between rounded px-3 py-2 mb-4"
            style={{ background: '#f0f4ff', border: '1px solid #c7d7ff' }}
          >
            <span className="fw-bold" style={{ fontSize: '1.1rem', letterSpacing: '0.05em' }}>
              {issuedCert?.certificateId}
            </span>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleCopyId}
              style={{ minWidth: '70px' }}
            >
              {copied ? (
                'Copied!'
              ) : (
                <>
                  <FontAwesomeIcon icon={faCopy} className="me-1" />
                  Copy
                </>
              )}
            </Button>
          </div>

          <div className="text-muted small mb-4">
            <div><strong>Domain:</strong> {issuedCert?.domain}</div>
            <div><strong>Issued By:</strong> {issuedCert?.issuedBy}</div>
            <div>
              <strong>Date:</strong>{' '}
              {issuedCert?.issueDate
                ? new Date(issuedCert.issueDate).toLocaleDateString()
                : '—'}
            </div>
          </div>

          <Button variant="primary" onClick={handleCloseSuccess} className="w-100">
            Issue Another Certificate
          </Button>
        </Modal.Body>
      </Modal>

      {/* ── Edit Modal ── */}
      <Modal show={showEditModal} onHide={closeEditModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Certificate</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditSubmit}>
          <Modal.Body>
            {editError && (
              <Alert variant="danger" dismissible onClose={() => setEditError('')}>
                {editError}
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Certificate ID</Form.Label>
              <Form.Control
                type="text"
                value={editForm.certificateId}
                onChange={(e) => handleEditChange('certificateId', e.target.value)}
                readOnly
                className="bg-light"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Issued To</Form.Label>
              <Form.Control
                type="text"
                value={editForm.internName}
                onChange={(e) => handleEditChange('internName', e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={editForm.domain}
                onChange={(e) => handleEditChange('domain', e.target.value)}
              >
                {ROLES.map((role) => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Issued Date</Form.Label>
              <Form.Control
                type="date"
                value={editForm.issueDate}
                onChange={(e) => handleEditChange('issueDate', e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Issued By</Form.Label>
              <Form.Control
                type="text"
                value={editForm.issuedBy}
                onChange={(e) => handleEditChange('issuedBy', e.target.value)}
                readOnly
                className="bg-light"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Control type="text" value="VALID" readOnly className="bg-light" />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeEditModal} disabled={editSubmitting}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={editSubmitting}>
              {editSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete modal */}
      {showDeleteModal && (
        <CommonDeleteModal
          id={selectedCertId}
          onClose={handleCloseDeleteModal}
          onDelete={handleDeleteSuccess}
          show={showDeleteModal}
          deleteUrl="api/admin/certificates"
        />
      )}
    </Container>
  );
};

export default IssueCertificate;
